import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ColorLensOutlinedIcon from '@mui/icons-material/ColorLensOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import FitScreenOutlinedIcon from '@mui/icons-material/FitScreenOutlined';
import GroupsIcon from '@mui/icons-material/Groups';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
  Box,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import uuid from 'react-uuid';
import type { CustomUser } from 'api/models/CustomUser';
import { ApiService } from 'api/services/ApiService';
import { DESC, VALUATION_DATE_KEY } from 'common/constants/analyst-dashboards';
import {
  PrimaryNavigationMenu,
  PrimaryNavigationMenuItem,
  TertiaryNavigation,
  TertiaryNavigationAddButton,
  TertiaryNavigationIconButton,
} from 'components/ui';
import type { TertiaryNavigationItem } from 'components/ui/TertiaryNavigation/TertiaryNavigation.types';
import { EventType } from 'context/EventBusContext/EventBusContext.type';
import { useEventSubscription } from 'context/EventBusContext/useEventSubscription';
import { useZoom } from 'context/ZoomContext';
import {
  DEFAULT_GOOGLE_CALENDAR_SHOW_VACATION_EVENTS,
  GOOGLE_CALENDAR_SHOW_VACATION_EVENTS_KEY,
} from 'dashboard409a/common/constants/calendar';
import { ExpandableSearch, ExpandableSearchHandle } from 'dashboard409a/components/ExpandableSearch';
import ModelsCalendarWithToolbar from 'dashboard409a/components/ModelsCalendar/ModelsCalendarWithToolbar';
import ModelsColorSelector from 'dashboard409a/components/ModelsColorSelector/ModelsColorSelector';
import { ModelsTable } from 'dashboard409a/components/ModelsTable/ModelsTable';
import {
  FilterTypeSelection,
  MODEL_FILTER_TYPES,
  ModelFilterType,
} from 'dashboard409a/components/ModelsTable/ModelsTable.types';
import { OperationsModal, useOperationsModal } from 'dashboard409a/components/OperationsModal';
import {
  calculateTableHeight,
  GenericWorkboard,
  useNoteHandler,
} from 'dashboard409a/components/ValuationsTable/common';
import ViewToggle, { ViewType } from 'dashboard409a/components/ViewToggle/ViewToggle';
import { useCompanyDashboardContext } from 'dashboard409a/context/CompanyDashboardContext';
import { useModelsWithValuationData } from 'dashboard409a/hooks/useModelsWithValuationData';
import { useMultiColumnSort } from 'dashboard409a/hooks/useMultiColumnSort';
import { CreateNoteRequest } from 'dashboard409a/services/attio.types';
import { RootStateType } from 'dashboard409a/states/store';
import {
  DEFAULT_FILTER_TYPE_SELECTION,
  normalizeFilterTypeSelection,
  normalizeModelFilterType,
} from 'dashboard409a/utilities/modelFilterNormalization';
import { useDebouncedValue } from 'hooks';
import { useLocalStorage } from 'services/hooks';
import { useLocalStorage as useLocalStorageHook } from 'services/hooks/useLocalStorage';
import { NormalizableDate, normalizeDate } from 'utilities/dateUtils';
import ModelsNewViewDialog from './ModelsNewViewDialog';
import {
  ACTIVE_MODEL_VIEW_KEY,
  DEFAULT_NEW_VIEW_NAME,
  DEFAULT_VIEW_ID,
  MODELS_SORT_KEY,
  MODELS_VIEW_KEY,
  MODELS_VIEWS_KEY,
} from '../common/constants';
import { hasViewChanged } from '../common/utils';
import { FulfillmentModelsLeaderboardModal } from '../FulfillmentModelsLeaderboard/FulfillmentModelsLeaderboardModal';
import { GoogleCalendarSettingsModal } from '../GoogleCalendarSettings/GoogleCalendarSettingsModal';
import { ModelView, ViewUserId } from '../ModelViewsSelector';
import { UserGroupsModal } from '../UserGroups/UserGroupsModal';

// Normalize legacy number[] or new ViewUserId[] formats
const normalizeViewUserIds = (ids: unknown): ViewUserId[] => {
  if (!Array.isArray(ids)) {
    return [];
  }
  return ids
    .map(id => {
      if (typeof id === 'number') {
        return { userId: id, groupId: null };
      }
      if (typeof id === 'object' && id !== null && 'userId' in id) {
        return id as ViewUserId;
      }
      return null;
    })
    .filter((id): id is ViewUserId => id !== null);
};

// Helper function to get or create Default view
const getDefaultView = (): ModelView => ({
  id: DEFAULT_VIEW_ID,
  name: 'Default',
  preparerIds: [],
  reviewerIds: [],
  searchStatus: [],
  fromDate: null,
  toDate: null,
  filterType: MODEL_FILTER_TYPES.ASSIGNED,
  filterTypeSelection: DEFAULT_FILTER_TYPE_SELECTION,
  columnVisibility: {},
});

// Helper function to ensure Default view exists in views array
const ensureDefaultView = (views: ModelView[]): ModelView[] => {
  // Normalize dates and user ID formats coming from localStorage
  const normalizedViews = views.map(v => ({
    ...v,
    fromDate: normalizeDate((v as unknown as { fromDate?: unknown }).fromDate as NormalizableDate),
    toDate: normalizeDate((v as unknown as { toDate?: unknown }).toDate as NormalizableDate),
    preparerIds: normalizeViewUserIds((v as unknown as { preparerIds?: unknown }).preparerIds),
    reviewerIds: normalizeViewUserIds((v as unknown as { reviewerIds?: unknown }).reviewerIds),
    filterType: normalizeModelFilterType((v as unknown as { filterType?: string }).filterType),
    filterTypeSelection: normalizeFilterTypeSelection(
      (v as unknown as { filterTypeSelection?: Record<string, unknown> }).filterTypeSelection,
      (v as unknown as { filterType?: string }).filterType
    ),
  }));

  const defaultView = normalizedViews.find(v => v.id === DEFAULT_VIEW_ID);
  if (!defaultView) {
    return [getDefaultView(), ...normalizedViews];
  }

  // Update Default view to ensure it has all required fields
  return normalizedViews.map(v =>
    v.id === DEFAULT_VIEW_ID
      ? {
        ...getDefaultView(),
        ...v,
        fromDate: (v as unknown as { fromDate?: unknown }).fromDate
          ? normalizeDate((v as unknown as { fromDate?: unknown }).fromDate as string | Date | null | undefined)
          : null,
        toDate: (v as unknown as { toDate?: unknown }).toDate
          ? normalizeDate((v as unknown as { toDate?: unknown }).toDate as string | Date | null | undefined)
          : null,
      }
      : v
  );
};

interface WorkboardModelsTableProps {
  // Filter type for models
  filterType?: ModelFilterType;
  // Initial preparer user IDs
  preparerUserIds?: number[];
  // Initial reviewer user IDs
  reviewerUserIds?: number[];
}

const WorkboardModelsTable = ({
  filterType: initialFilterType = MODEL_FILTER_TYPES.ASSIGNED,
  preparerUserIds: initialPreparerUserIds,
  reviewerUserIds: initialReviewerUserIds,
}: WorkboardModelsTableProps) => {
  // Use shared multi-column sort hook
  const { sortCriteria, orderBy, order, handleRequestSort } = useMultiColumnSort({
    storageKey: MODELS_SORT_KEY,
    defaultOrderBy: VALUATION_DATE_KEY,
    defaultOrder: DESC,
  });

  const [filterType, setFilterType] = useState<ModelFilterType>(initialFilterType);
  const [filterTypeSelection, setFilterTypeSelection] = useState<FilterTypeSelection>(DEFAULT_FILTER_TYPE_SELECTION);
  const [preparerIds, setPreparerIds] = useState<number[]>(initialPreparerUserIds ?? []);
  const [reviewerIds, setReviewerIds] = useState<number[]>(initialReviewerUserIds ?? []);
  const searchRef = useRef<ExpandableSearchHandle>(null);
  const [searchParam, setSearchParam] = useState('');
  const debouncedSearchParam = useDebouncedValue(searchParam, 500);
  const [searchStatus, setSearchStatus] = useState<string[]>([]);
  const [isGoogleCalendarSettingsModalOpen, setIsGoogleCalendarSettingsModalOpen] = useState(false);
  const [isLeaderboardModalOpen, setIsLeaderboardModalOpen] = useState(false);
  const [showGoogleVacationEvents, setShowGoogleVacationEvents] = useLocalStorage<boolean>(
    GOOGLE_CALENDAR_SHOW_VACATION_EVENTS_KEY,
    DEFAULT_GOOGLE_CALENDAR_SHOW_VACATION_EVENTS
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        searchRef.current?.expand();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [currentViewId, setCurrentViewId] = useState<string | null>(DEFAULT_VIEW_ID);
  const [views, setViews, , isViewsReady] = useLocalStorage<ModelView[]>(MODELS_VIEWS_KEY, [getDefaultView()]);

  const defaultNewViewName = useMemo(() => {
    const lowerNames = views.map(v => v.name.toLowerCase());
    const base = DEFAULT_NEW_VIEW_NAME;
    if (!lowerNames.includes(base.toLowerCase())) return base;
    const firstCandidate = `${base} (Copy)`;
    if (!lowerNames.includes(firstCandidate.toLowerCase())) return firstCandidate;
    let i = 2;
    while (lowerNames.includes(`${base} (Copy ${i})`.toLowerCase())) {
      i++;
    }
    return `${base} (Copy ${i})`;
  }, [views]);
  const [activeModelView, setActiveModelView, , isActiveModelViewReady] = useLocalStorage<ModelView | null>(
    ACTIVE_MODEL_VIEW_KEY,
    null
  );
  const [isFiltering, setIsFiltering] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
  const hasRestoredActiveView = useRef(false);

  // Ensure Default view always exists and initialize with saved active view after IndexedDB hydration
  useEffect(() => {
    if (!isViewsReady || !isActiveModelViewReady || hasRestoredActiveView.current) {
      return;
    }
    hasRestoredActiveView.current = true;

    const updatedViews = ensureDefaultView(views);
    if (
      updatedViews.length !== views.length ||
      updatedViews.some(
        (v, i) =>
          v.id !== views[i]?.id || (v.id === DEFAULT_VIEW_ID && (v.fromDate === undefined || v.toDate === undefined))
      )
    ) {
      setViews(updatedViews);
    }

    // Check if we should load the saved active view
    let viewToLoad: ModelView | null = null;
    if (activeModelView) {
      // Check if the active view still exists in the views array
      const viewExists = updatedViews.find(v => v.id === activeModelView.id);
      if (viewExists) {
        viewToLoad = viewExists;
      }
    }

    // If no active view to load, use default view
    viewToLoad ??= updatedViews.find(v => v.id === DEFAULT_VIEW_ID) || getDefaultView();

    setCurrentViewId(viewToLoad.id);
    setPreparerIds(viewToLoad.preparerIds.map(p => p.userId));
    setReviewerIds(viewToLoad.reviewerIds.map(r => r.userId));
    let statusArray: string[] = [];
    if (Array.isArray(viewToLoad.searchStatus)) {
      statusArray = viewToLoad.searchStatus;
    } else if (viewToLoad.searchStatus) {
      statusArray = [viewToLoad.searchStatus];
    }
    setSearchStatus(statusArray);
    setFromDate(viewToLoad.fromDate);
    setToDate(viewToLoad.toDate);
    const filterTypeValue = normalizeModelFilterType(viewToLoad.filterType);
    setFilterType(filterTypeValue);
    setFilterTypeSelection(
      normalizeFilterTypeSelection(
        viewToLoad.filterTypeSelection,
        filterTypeValue
      )
    );
    setColumnVisibility(viewToLoad.columnVisibility ?? {});
  }, [activeModelView, isActiveModelViewReady, isViewsReady, setViews, views]);
  const [storedView, setStoredView] = useLocalStorage<ViewType>(MODELS_VIEW_KEY, 'calendar');
  const view: ViewType = storedView === 'calendar' || storedView === 'table' ? storedView : 'calendar';

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isGroupsModalOpen, setIsGroupsModalOpen] = useState(false);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState<null | HTMLElement>(null);
  const settingsMenuOpen = Boolean(settingsMenuAnchor);
  const [colorPaletteModalOpen, setColorPaletteModalOpen] = useState(false);

  // Track color palette changes to force re-render when Done is clicked
  const [paletteVersion, setPaletteVersion] = useState(0);
  const [isPaletteChanging, setIsPaletteChanging] = useState(false);

  const handlePaletteClose = useCallback(() => {
    setIsPaletteChanging(true);
    setPaletteVersion(prev => prev + 1);
  }, []);

  // Clear the palette-changing flag after the remount has painted
  useEffect(() => {
    if (!isPaletteChanging) return;
    const raf = requestAnimationFrame(() => setIsPaletteChanging(false));
    return () => cancelAnimationFrame(raf);
  }, [isPaletteChanging]);
  // Column widths for resizable columns
  const [columnWidths, setColumnWidths] = useLocalStorageHook<Record<string, number>>('modelsColumnWidths', {});

  const [tableKey, setTableKey] = useState(0);

  const handleColumnWidthChange = useCallback(
    (columnId: string, newWidth: number) => {
      setColumnWidths(prev => ({
        ...prev,
        [columnId]: newWidth,
      }));
    },
    [setColumnWidths]
  );

  const { data: staffUsers = [] } = useQuery<CustomUser[]>({
    queryKey: ['users-list', 'staff'],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList();
      return response.filter(u => !u.email?.includes('+')) as CustomUser[];
    },
    staleTime: 60 * 60 * 1000,
  });

  const isSuperuser = useSelector((state: RootStateType) => state.user.is_superuser);
  const { zoomLevel } = useZoom();
  const { setTertiaryNavNode } = useCompanyDashboardContext();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuViewId, setMenuViewId] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [pendingEditView, setPendingEditView] = useState<ModelView | null>(null);

  const calendarPanelSx = useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'column',
      height: calculateTableHeight(zoomLevel, 190),
      minHeight: '560px',
      overflow: 'hidden',
    }),
    [zoomLevel]
  );

  const {
    rows: models,
    isLoading,
    isLoadingMore,
    updateModelNote,
    updateModelStatus,
    updateModelDueDate,
    refetch: refetchModels,
  } = useModelsWithValuationData({
    filterType,
    filterTypeSelection,
    searchStatus,
    preparerUserIds: preparerIds,
    reviewerUserIds: reviewerIds,
    disableSorting: view === 'calendar',
    orderBy,
    order,
    sortCriteria,
    fromDate: fromDate && !Number.isNaN(fromDate.getTime()) ? fromDate.toISOString().split('T')[0] : undefined,
    toDate: toDate && !Number.isNaN(toDate.getTime()) ? toDate.toISOString().split('T')[0] : undefined,
    companyName: debouncedSearchParam.trim() || undefined,
  });

  useEventSubscription(
    EventType.WORKBOARD_VALUATION_UPDATED,
    useCallback(() => {
      refetchModels();
    }, [refetchModels])
  );

  const handleAutoFitColumns = useCallback(() => {
    // Calculate optimal width for Company Name based on longest company name in data
    const longestCompanyName = models.reduce((longest, row) => {
      const name = row.company_name || '';
      return name.length > longest.length ? name : longest;
    }, '');

    // Calculate width: ~8px per character + padding (cell padding + some buffer)
    const charWidth = 8;
    const padding = 32;
    const companyNameWidth = Math.max(150, Math.min(400, longestCompanyName.length * charWidth + padding));

    // Clear all custom column widths and set Company Name to calculated width
    setColumnWidths({
      company_name: companyNameWidth,
    });

    // Force table remount to recalculate column widths
    setTableKey(prev => prev + 1);
  }, [setColumnWidths, models]);

  // Clear filtering state after models are filtered and rendered
  useEffect(() => {
    if (isFiltering) {
      setIsFiltering(false);
    }
  }, [models, isFiltering]);

  useEffect(() => {
    if (isRefreshing && !isLoading) {
      setIsRefreshing(false);
    }
  }, [isLoading, isRefreshing]);

  const { isNoteLoading, handleNoteSave: handleNoteSaveGeneric } = useNoteHandler({
    onSuccess: (id, noteContent, createdNote) => {
      if (createdNote?.fulfillment) {
        const fulfillmentId = createdNote.fulfillment;
        const noteId = createdNote.id;
        updateModelNote(fulfillmentId, noteContent, noteId);
      } else {
        updateModelNote(id as number, noteContent);
      }
    },
  });

  const handleNoteSave = async (fulfillment: number, noteContent: string, currentNote?: string) => {
    if (!fulfillment) {
      throw new Error('Invalid fulfillment ID. Cannot save note.');
    }

    const request: CreateNoteRequest = {
      content: noteContent,
      fulfillment,
      reference_type: 'fu',
    };

    const currentModel = models.find(m => m.id === fulfillment);
    // A note DB record exists if note is non-null (regardless of content being empty)
    const hasExistingNote = currentModel?.note != null;

    const existingNoteId = hasExistingNote && currentModel?.note?.id ? currentModel.note.id.toString() : undefined;

    await handleNoteSaveGeneric(fulfillment, noteContent, currentNote, request, hasExistingNote, existingNoteId);
  };

  const handleStatusUpdate = (modelId: number, newStatus: number) => {
    updateModelStatus(modelId, newStatus);
  };

  const { openModal, selectedModel, handleOpenModal, handleCloseModal } = useOperationsModal({ models });

  const handleEventDrop = async (modelId: number, newDueDate: string) => {
    updateModelDueDate(modelId, newDueDate);
  };

  const handleLoadView = useCallback((savedView: ModelView) => {
    setIsFiltering(true);
    setPreparerIds(savedView.preparerIds.map(p => p.userId));
    setReviewerIds(savedView.reviewerIds.map(r => r.userId));
    let statusArray: string[] = [];
    if (Array.isArray(savedView.searchStatus)) {
      statusArray = savedView.searchStatus;
    } else if (savedView.searchStatus) {
      statusArray = [savedView.searchStatus];
    }
    setSearchStatus(statusArray);
    setFromDate(normalizeDate(savedView.fromDate));
    setToDate(normalizeDate(savedView.toDate));
    const filterTypeValue = normalizeModelFilterType(savedView.filterType);
    setFilterType(filterTypeValue);
    setFilterTypeSelection(
      normalizeFilterTypeSelection(savedView.filterTypeSelection, filterTypeValue)
    );
    setColumnVisibility(savedView.columnVisibility ?? {});
    setCurrentViewId(savedView.id);
    setActiveModelView(savedView);
  }, [setActiveModelView]);

  const handleCreateViewFromDialog = useCallback(
    (view: ModelView) => {
      setViews(prev => {
        const updated = ensureDefaultView(prev);
        return [...updated, view];
      });
      handleLoadView(view);
    },
    [handleLoadView, setViews]
  );

  const handleEditView = useCallback(
    (viewToEdit: ModelView) => {
      // Use viewToEdit as the source of truth. Overwriting with local state here
      // caused stale-closure bugs where the wrong criteria were persisted.
      const currentView = views.find(v => v.id === viewToEdit.id);
      if (currentView && !hasViewChanged(currentView, viewToEdit)) {
        return;
      }

      setViews(prev => {
        const updated = prev.map(v => (v.id === viewToEdit.id ? viewToEdit : v));
        return ensureDefaultView(updated);
      });
    },
    [setViews, views]
  );

  const handleEditViewConfirm = useCallback(
    (view: ModelView) => {
      handleEditView(view);
      if (currentViewId === view.id) {
        setColumnVisibility(view.columnVisibility ?? {});
      }
    },
    [handleEditView, currentViewId]
  );

  const handleDeleteView = useCallback(
    (viewId: string) => {
      if (viewId === DEFAULT_VIEW_ID) {
        return; // Cannot delete Default view
      }
      setViews(prev => {
        const filtered = prev.filter(v => v.id !== viewId);
        return ensureDefaultView(filtered);
      });
      if (currentViewId === viewId) {
        setCurrentViewId(DEFAULT_VIEW_ID);
        handleLoadView(getDefaultView());
      }
    },
    [currentViewId, setViews, handleLoadView]
  );

  const handleGroupUpdated = useCallback((syncedViews: ModelView[] | null) => {
    if (!currentViewId || !syncedViews?.length) {
      return;
    }
    const updatedViews = ensureDefaultView(syncedViews);
    const currentView = updatedViews.find(v => v.id === currentViewId);
    if (currentView) {
      handleLoadView(currentView);
    }
  }, [currentViewId, handleLoadView]);

  const handleCloneView = useCallback(
    (viewId: string) => {
      const source = views.find(v => v.id === viewId);
      if (!source) return;
      const existingNames = new Set(views.map(v => v.name));
      let cloneName = `${source.name} (Copy)`;
      let i = 2;
      while (existingNames.has(cloneName)) {
        cloneName = `${source.name} (Copy ${i++})`;
      }
      const cloned: ModelView = { ...source, id: uuid(), name: cloneName };
      setViews(prev => ensureDefaultView([...prev, cloned]));
    },
    [views, setViews]
  );

  const handleTabChange = useCallback(
    (selectedId: string) => {
      const view = views.find(v => v.id === selectedId);
      if (view) {
        handleLoadView(view);
      }
    },
    [views, handleLoadView]
  );

  const handleAddView = useCallback(() => {
    setIsCreateDialogOpen(true);
  }, []);

  const handleItemMenuClick = useCallback((event: ReactMouseEvent<HTMLButtonElement>, item: TertiaryNavigationItem) => {
    event.stopPropagation();
    setMenuViewId(item.value);
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setMenuViewId(null);
  }, []);

  const handleEditViewFromMenu = useCallback(() => {
    if (!menuViewId) return;
    const view = views.find(v => v.id === menuViewId);
    if (!view) return;
    setPendingEditView(view);
    setMenuAnchor(null);
    setMenuViewId(null);
    setIsEditDialogOpen(true);
  }, [menuViewId, views]);

  const handleCloneViewFromMenu = useCallback(() => {
    handleMenuClose();
    if (menuViewId) handleCloneView(menuViewId);
  }, [handleMenuClose, menuViewId, handleCloneView]);

  const handleDeleteViewFromMenu = useCallback(() => {
    handleMenuClose();
    if (menuViewId) handleDeleteView(menuViewId);
  }, [handleMenuClose, menuViewId, handleDeleteView]);


  const tertiaryItems = useMemo<TertiaryNavigationItem[]>(
    () =>
      views.map(v => ({
        label: v.name,
        value: v.id,
        showMenu: v.id !== DEFAULT_VIEW_ID,
        isClosable: v.id !== DEFAULT_VIEW_ID,
      })),
    [views]
  );

  const tertiaryNavActionsNode = useMemo(
    () => (
      <Box display="flex" alignItems="center" gap={1}>
        <ViewToggle view={view} onChange={setStoredView} />
        <TertiaryNavigationIconButton
          aria-controls={settingsMenuOpen ? 'workboard-models-options-menu' : undefined}
          aria-expanded={settingsMenuOpen ? 'true' : undefined}
          aria-haspopup="true"
          ariaLabel="Open workboard options"
          icon={<SettingsOutlinedIcon fontSize="inherit" />}
          tooltipTitle="Options"
          variant="toolbar"
          onClick={e => setSettingsMenuAnchor(e.currentTarget)}
        />
        <Menu
          id="workboard-models-options-menu"
          anchorEl={settingsMenuAnchor}
          open={settingsMenuOpen}
          onClose={() => setSettingsMenuAnchor(null)}
          slotProps={{ list: { dense: true } }}>
          <MenuItem onClick={() => {
            setSettingsMenuAnchor(null);
            setColorPaletteModalOpen(true);
          }}>
            <ListItemIcon><ColorLensOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Calendar colors</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => {
            setSettingsMenuAnchor(null);
            handleAutoFitColumns();
          }}>
            <ListItemIcon><FitScreenOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Auto-fit columns</ListItemText>
          </MenuItem>
          {isSuperuser && (
            <MenuItem onClick={() => {
              setSettingsMenuAnchor(null);
              setIsGroupsModalOpen(true);
            }}>
              <ListItemIcon><GroupsIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Manage user groups</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={() => {
            setSettingsMenuAnchor(null);
            setIsGoogleCalendarSettingsModalOpen(true);
          }}>
            <ListItemIcon><CalendarMonthOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Google Calendar settings</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setSettingsMenuAnchor(null);
              setIsLeaderboardModalOpen(true);
            }}
            data-testid="workboard-models-leaderboard-menu-item">
            <ListItemIcon><EmojiEventsOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Leaderboard</ListItemText>
          </MenuItem>
        </Menu>
        <ModelsColorSelector
          hideTrigger
          modalOpen={colorPaletteModalOpen}
          onModalOpenChange={setColorPaletteModalOpen}
          onClose={handlePaletteClose}
        />
        <ExpandableSearch
          ref={searchRef}
          value={searchParam}
          onChange={e => setSearchParam(e.target.value)}
          placeholder="Search by company name..."
        />
        <TertiaryNavigationIconButton
          ariaLabel="Refresh data"
          disabled={isRefreshing}
          icon={isRefreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon fontSize="inherit" />}
          tooltipTitle="Refresh data"
          variant="toolbar"
          onClick={() => {
            setIsRefreshing(true);
            refetchModels();
          }}
        />
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: 'action.hover',
            borderRadius: '8px',
            display: 'inline-flex',
            px: 1.5,
            py: 0.5,
            whiteSpace: 'nowrap',
          }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {(() => {
              if (isFiltering || isLoading || isRefreshing) return 'Loading models...';
              if (isLoadingMore) return `Loading models: ${models.length.toLocaleString()} loaded...`;
              return `Total models: ${models.length.toLocaleString()}`;
            })()}
          </Typography>
        </Box>
      </Box>
    ),
    [
      view, settingsMenuOpen, settingsMenuAnchor, isSuperuser, colorPaletteModalOpen,
      handlePaletteClose, searchParam, isRefreshing, refetchModels,
      isFiltering, isLoading, isLoadingMore, models.length, handleAutoFitColumns, setStoredView,
    ]
  );

  const tertiaryNavNode = useMemo(
    () => (
      <TertiaryNavigation
        items={tertiaryItems}
        leftActions={<TertiaryNavigationAddButton onClick={handleAddView} />}
        actions={tertiaryNavActionsNode}
        value={currentViewId ?? DEFAULT_VIEW_ID}
        onChange={handleTabChange}
        onItemClose={(_, item) => handleDeleteView(item.value)}
        onItemMenuClick={handleItemMenuClick}
        zoomLevel={zoomLevel}
      />
    ),
    [tertiaryItems, handleAddView, tertiaryNavActionsNode, currentViewId, handleTabChange, handleDeleteView, handleItemMenuClick, zoomLevel]
  );

  useEffect(() => {
    setTertiaryNavNode?.(tertiaryNavNode);
    return () => {
      setTertiaryNavNode?.(null);
    };
  }, [setTertiaryNavNode, tertiaryNavNode]);

  return (
    <GenericWorkboard>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {view === 'calendar' ? (
          <Box sx={calendarPanelSx}>
            <ModelsCalendarWithToolbar
              key={paletteVersion}
              models={models}
              isLoading={isLoading || isFiltering || isRefreshing || isPaletteChanging}
              onOpenPreview={handleOpenModal}
              onEventDrop={handleEventDrop}
              showGoogleVacationEvents={showGoogleVacationEvents ?? DEFAULT_GOOGLE_CALENDAR_SHOW_VACATION_EVENTS}
            />
          </Box>
        ) : (
          <ModelsTable
            isLoading={isLoading || isFiltering || isRefreshing}
            rows={models}
            onNoteSave={handleNoteSave}
            orderBy={orderBy}
            order={order}
            sortCriteria={sortCriteria}
            onRequestSort={handleRequestSort}
            onStatusUpdate={handleStatusUpdate}
            isNoteLoading={isNoteLoading}
            onRowClick={handleOpenModal}
            columnWidths={columnWidths}
            onColumnWidthChange={handleColumnWidthChange}
            tableKey={tableKey}
            columnVisibility={columnVisibility}
          />
        )}
      </Box>
      <OperationsModal open={openModal} onClose={handleCloseModal} data={selectedModel} isFromModels />
      {isSuperuser && (
        <UserGroupsModal
          open={isGroupsModalOpen}
          onClose={() => setIsGroupsModalOpen(false)}
          onGroupUpdated={handleGroupUpdated}
        />
      )}
      <GoogleCalendarSettingsModal
        open={isGoogleCalendarSettingsModalOpen}
        showVacationEvents={showGoogleVacationEvents ?? DEFAULT_GOOGLE_CALENDAR_SHOW_VACATION_EVENTS}
        onClose={() => setIsGoogleCalendarSettingsModalOpen(false)}
        onShowVacationEventsChange={setShowGoogleVacationEvents}
      />
      <FulfillmentModelsLeaderboardModal
        open={isLeaderboardModalOpen}
        onClose={() => setIsLeaderboardModalOpen(false)}
      />
      <PrimaryNavigationMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor && menuViewId)}
        onClose={handleMenuClose}
        menuMinWidth={0}
        menuWidth="content"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <PrimaryNavigationMenuItem icon={<EditIcon fontSize="inherit" />} onClick={handleEditViewFromMenu}>
          Edit View
        </PrimaryNavigationMenuItem>
        <PrimaryNavigationMenuItem icon={<ContentCopyIcon fontSize="inherit" />} onClick={handleCloneViewFromMenu}>
          Clone
        </PrimaryNavigationMenuItem>
        <Divider />
        <PrimaryNavigationMenuItem icon={<DeleteIcon fontSize="inherit" />} onClick={handleDeleteViewFromMenu} tone="danger">
          Delete
        </PrimaryNavigationMenuItem>
      </PrimaryNavigationMenu>
      <ModelsNewViewDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCreateView={handleCreateViewFromDialog}
        currentColumnVisibility={columnVisibility}
        staffUsers={staffUsers}
        defaultName={defaultNewViewName}
      />
      <ModelsNewViewDialog
        open={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setPendingEditView(null);
        }}
        onCreateView={handleEditViewConfirm}
        currentColumnVisibility={columnVisibility}
        staffUsers={staffUsers}
        initialView={pendingEditView ?? undefined}
      />
    </GenericWorkboard>
  );
};

export default WorkboardModelsTable;
